import { useEffect, useState } from "react";
import { ACTIVITY_NOTIFICATIONS } from "@/data/content";

// ═══════════════════════════════════════════════════════════════════════
// SOCIAL-PROOF WIDGET — rotation timings (tune here):
//   first notification ~7s after landing, visible 4s, next one 2s later.
// ═══════════════════════════════════════════════════════════════════════
const FIRST_DELAY = 7000;
const VISIBLE_MS = 4000;
const GAP_MS = 2000;

export default function ActivityNotifications({
  notifications = ACTIVITY_NOTIFICATIONS,
}) {
  const [index, setIndex] = useState(null); // null = cycle not started
  const [visible, setVisible] = useState(false);
  const [overlaysForm, setOverlaysForm] = useState(false);
  const [nearBottom, setNearBottom] = useState(false);
  const [reduced] = useState(
    () => window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );

  // Suppress the widget where it would cover interactive elements.
  useEffect(() => {
    const targets = [
      document.getElementById("qualify"),
      document.querySelector("[data-testid=bottom-cta]"),
    ].filter(Boolean);
    const observer = new IntersectionObserver(
      (entries) => setOverlaysForm(entries.some((e) => e.isIntersecting)),
      { threshold: 0.05 },
    );
    targets.forEach((t) => observer.observe(t));

    const onScroll = () => {
      const doc = document.documentElement;
      setNearBottom(
        window.scrollY + window.innerHeight > doc.scrollHeight - 320,
      );
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  // Rotation loop.
  useEffect(() => {
    let timeout;
    let cancelled = false;
    let i = Math.floor(Math.random() * notifications.length);

    const show = () => {
      if (cancelled) return;
      setIndex(i);
      setVisible(true);
      timeout = setTimeout(() => {
        if (cancelled) return;
        setVisible(false);
        timeout = setTimeout(() => {
          i = (i + 1) % notifications.length;
          show();
        }, GAP_MS);
      }, VISIBLE_MS);
    };

    timeout = setTimeout(show, FIRST_DELAY);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, []);

  if (index === null) return null;
  const shown = visible && !overlaysForm && !nearBottom;
  const entry = notifications[index];

  return (
    <div
      aria-live="polite"
      className={`fixed bottom-4 left-4 z-50 w-[320px] max-w-[calc(100vw-2rem)] sm:bottom-6 sm:left-6 ${reduced ? "" : "transition-all duration-500 ease-out"} ${shown ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-3 opacity-0"}`}
      data-testid="activity-notification"
    >
      <div className="flex items-center gap-3 rounded-lg border border-line bg-ink-900/95 px-4 py-3.5 shadow-panel backdrop-blur-sm">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-line bg-ink-800">
          <span className="font-display text-base font-semibold text-chrome" aria-hidden="true">
            7
          </span>
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-fg" data-testid="activity-name">
            {entry.firstName} from {entry.location}
          </p>
          <p className="truncate text-xs text-fg-muted" data-testid="activity-action">
            {entry.action}
          </p>
          <p className="mt-1 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-fg-dim">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand" aria-hidden="true" />
            Just now
          </p>
        </div>
      </div>
    </div>
  );
}
